/** The answer before the evidence. Real fonts, real 16:9 slides, and controls
 * that let the user keep, reject, or reshuffle the recommendation without
 * losing the engine trace underneath. */

import { useMemo, useState, type CSSProperties } from "react";

import type { DirectionCard, TypeLibraryRole, TypeRecommendationItem, TypeStudioSession } from "@core/index.js";

import { useStudio } from "../app/store";
import { SlidePreview } from "../components/SlidePreview";
import { fontSourcesForSystem } from "../wiring/catalog";
import { previewCopyFor } from "../wiring/engines";
import { exactFontsForRecommendation, roleLabel, selectedTypeDirection, stacksForDirection, stacksForRecommendation, typeLibrary } from "../wiring/typeLibrary";
import { EvidenceDrawer } from "./EvidenceDrawer";

const SavedTypeResult = ({ item }: { item: TypeRecommendationItem }) => {
  const sources = fontSourcesForSystem(item.systemId);
  const exactFonts = exactFontsForRecommendation(item);
  const exactSaved = Object.values(exactFonts).every(Boolean);
  const exactStacks = stacksForRecommendation(item);
  return (
    <section className="result-panel result-panel--type" aria-labelledby="type-result-title">
      <p className="eyebrow">Saved type direction</p>
      <h3 id="type-result-title" style={exactSaved ? { fontFamily: exactStacks.display } : undefined}>{item.name}</h3>
      <p className="result-reason">{exactSaved
        ? "The exact local faces are back. Your chosen direction survives; shuffle history, stars and locks were never hidden inside the project file."
        : "This older project keeps its original recommendation. Start a new Type run to use the exact 64-family studio."}</p>
      <dl className="type-roles">
        {(["display", "body", "utility"] as const).map((role) => {
          const exact = exactFonts[role];
          const source = sources.find((font) => font.id === item.roles[role].fontId);
          return <div key={role}><dt>{roleLabel(role)}</dt><dd style={exact ? { fontFamily: exactStacks[role] } : undefined}>{exact?.family ?? source?.family ?? item.roles[role].fontId}</dd></div>;
        })}
      </dl>
      {exactSaved ? <p className="truth-note">Exact in this browser again. Re-run Type only when you want a new set of five—not because reopening broke the letterforms.</p> : null}
    </section>
  );
};

const tuningWeightKey: Record<TypeLibraryRole, "displayWeight" | "bodyWeight" | "utilityWeight"> = {
  display: "displayWeight",
  body: "bodyWeight",
  utility: "utilityWeight",
};

const ExpertTypeLab = ({ session }: { session: TypeStudioSession }) => {
  const { state, dispatch } = useStudio();
  const [role, setRole] = useState<TypeLibraryRole>("display");
  const [query, setQuery] = useState("");
  const direction = selectedTypeDirection(session);
  const fonts = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return typeLibrary.fonts
      .filter((font) => font.roles.includes(role) && !session.current.excludedFontIds.includes(font.id))
      .filter((font) => !term || [font.family, font.designer, font.class, ...font.tones].some((value) => value.toLocaleLowerCase().includes(term)))
      .sort((left, right) => Number(session.current.starredFontIds.includes(right.id)) - Number(session.current.starredFontIds.includes(left.id)) || left.family.localeCompare(right.family));
  }, [query, role, session.current.excludedFontIds, session.current.starredFontIds]);

  if (!direction) return null;
  const activeFont = direction.roles[role];
  const weightKey = tuningWeightKey[role];
  const activeWeight = state.typeTuning[weightKey];

  const useFont = (fontId: string) => {
    const font = typeLibrary.fonts.find((candidate) => candidate.id === fontId);
    if (!font) return;
    const nextWeight = font.weights.includes(activeWeight)
      ? activeWeight
      : font.weights.reduce((closest, value) => Math.abs(value - activeWeight) < Math.abs(closest - activeWeight) ? value : closest, font.weights[0] ?? 400);
    dispatch({ type: "typeStudioAction", action: { type: "setRoleFont", role, fontId } });
    dispatch({
      type: "setTypeTuning",
      tuning: {
        [weightKey]: nextWeight,
        axisValues: { ...state.typeTuning.axisValues, [role]: {} },
      },
    });
  };

  const setAxis = (tag: string, value: number) => dispatch({
    type: "setTypeTuning",
    tuning: {
      axisValues: {
        ...state.typeTuning.axisValues,
        [role]: { ...state.typeTuning.axisValues[role], [tag]: value },
      },
    },
  });

  return (
    <details className="disclosure expert-lab expert-lab--type">
      <summary>Open the expert fitting room</summary>
      <div className="disclosure-body expert-lab__body">
        <header className="expert-lab__intro">
          <div><p className="eyebrow">EXPERT MODE</p><h4>Choose the face. Tune the fit. Keep the proof visible.</h4></div>
          <p>This is the longer bench: every family is exact, every available weight comes from the locked files, and every adjustment stays reversible.</p>
        </header>

        <div className="expert-role-tabs" role="tablist" aria-label="Typography roles">
          {(["display", "body", "utility"] as const).map((item) => (
            <button key={item} type="button" role="tab" aria-selected={role === item} onClick={() => setRole(item)}>{roleLabel(item)} <span>{direction.roles[item].family}</span></button>
          ))}
        </div>

        <div className="expert-font-browser">
          <div className="expert-font-browser__head">
            <label className="field"><span>Search the exact library</span><input className="text-input" type="search" value={query} placeholder="Family, maker, mood…" onChange={(event) => setQuery(event.target.value)} /></label>
            <p><strong>{fonts.length}</strong> eligible {fonts.length === 1 ? "family" : "families"} for this role. <strong>{activeFont.family}</strong> is doing the work now; search only when you want the crowd to thin out.</p>
          </div>
          <div className="expert-font-grid" role="list" aria-label={`Families available for ${role}`}>
            {fonts.map((font) => {
              const active = font.id === activeFont.id;
              const starred = session.current.starredFontIds.includes(font.id);
              return (
                <article key={font.id} role="listitem" className="expert-font-card" data-active={active} style={{ "--expert-font": `"${font.family}", ${font.category === "SERIF" ? "serif" : "sans-serif"}` } as CSSProperties}>
                  <button type="button" className="expert-font-card__choose" aria-pressed={active} onClick={() => useFont(font.id)}>
                    <span>{font.family}</span><strong>{role === "utility" ? "Q3·48%" : "Ag"}</strong><small>{font.designer} · {font.class.replaceAll("-", " ")}</small>
                  </button>
                  <button type="button" className="expert-font-card__star" aria-label={`${starred ? "Remove" : "Add"} ${font.family} ${starred ? "from" : "to"} saved fonts`} aria-pressed={starred} onClick={() => dispatch({ type: "typeStudioAction", action: { type: "toggleStarFont", fontId: font.id } })}>{starred ? "★" : "☆"}</button>
                </article>
              );
            })}
          </div>
          {fonts.length === 0 ? <p className="truth-note">No exact family matches that search. Clear a word or restore a rejected family; the recommendation itself is still safe.</p> : null}
        </div>

        <div className="expert-controls-grid">
          <section className="expert-control-panel">
            <p className="eyebrow">THE EXACT FILE</p>
            <h4>{activeFont.family}</h4>
            <label className="expert-select"><span>Weight</span><select value={activeWeight} onChange={(event) => dispatch({ type: "setTypeTuning", tuning: { [weightKey]: Number(event.target.value) } })}>{activeFont.weights.map((weight) => <option key={weight} value={weight}>{weight}</option>)}</select></label>
            {activeFont.axes.length > 0 ? (
              <div className="axis-controls">
                {activeFont.axes.map((axis) => {
                  const value = state.typeTuning.axisValues[role][axis.tag] ?? (axis.min + axis.max) / 2;
                  return <label key={axis.tag}><span>{axis.tag} <output>{Number(value.toFixed(1))}</output></span><input type="range" min={axis.min} max={axis.max} step={(axis.max - axis.min) > 20 ? 1 : 0.1} value={value} onChange={(event) => setAxis(axis.tag, Number(event.target.value))} /></label>;
                })}
              </div>
            ) : <p className="truth-note">No variable axes in this locked family. The available static weights above are the honest controls.</p>}
            <dl className="font-file-facts"><div><dt>Files</dt><dd>{activeFont.files.length}</dd></div><div><dt>Weights</dt><dd>{activeFont.weights.length}</dd></div><div><dt>Italic</dt><dd>{activeFont.hasItalic ? "Yes" : "No"}</dd></div><div><dt>Coverage</dt><dd>{activeFont.subsets.join(", ").replaceAll("-ext", "+")}</dd></div></dl>
            <p className="font-source-proof"><span>{activeFont.designer} · {activeFont.license.spdx} · {activeFont.source.provider}</span><a href={activeFont.source.upstreamRepository ?? activeFont.source.metadataUrl} target="_blank" rel="noreferrer">Official source and license ↗</a></p>
          </section>

          <section className="expert-control-panel">
            <p className="eyebrow">THE FIT</p>
            <h4>Adjust the system, not just the font.</h4>
            <label><span>Headline scale <output>{Math.round(state.typeTuning.scale * 100)}%</output></span><input type="range" min="0.8" max="1.25" step="0.01" value={state.typeTuning.scale} onChange={(event) => dispatch({ type: "setTypeTuning", tuning: { scale: Number(event.target.value) } })} /></label>
            <label><span>Body size <output>{Math.round(state.typeTuning.bodySize * 100)}%</output></span><input type="range" min="0.85" max="1.2" step="0.01" value={state.typeTuning.bodySize} onChange={(event) => dispatch({ type: "setTypeTuning", tuning: { bodySize: Number(event.target.value) } })} /></label>
            <label><span>Leading <output>{state.typeTuning.lineHeight.toFixed(2)}</output></span><input type="range" min="1.05" max="1.65" step="0.01" value={state.typeTuning.lineHeight} onChange={(event) => dispatch({ type: "setTypeTuning", tuning: { lineHeight: Number(event.target.value) } })} /></label>
            <label><span>Tracking <output>{state.typeTuning.tracking.toFixed(3)}em</output></span><input type="range" min="-0.04" max="0.08" step="0.002" value={state.typeTuning.tracking} onChange={(event) => dispatch({ type: "setTypeTuning", tuning: { tracking: Number(event.target.value) } })} /></label>
            <label><span>Reading measure <output>{state.typeTuning.measure}%</output></span><input type="range" min="42" max="78" step="1" value={state.typeTuning.measure} onChange={(event) => dispatch({ type: "setTypeTuning", tuning: { measure: Number(event.target.value) } })} /></label>
            <div className="segmented-control" aria-label="Slide alignment"><button type="button" aria-pressed={state.typeTuning.alignment === "left"} onClick={() => dispatch({ type: "setTypeTuning", tuning: { alignment: "left" } })}>Left</button><button type="button" aria-pressed={state.typeTuning.alignment === "center"} onClick={() => dispatch({ type: "setTypeTuning", tuning: { alignment: "center" } })}>Center</button></div>
            <button type="button" className="text-link" onClick={() => dispatch({ type: "resetTypeTuning" })}>Reset the fit</button>
          </section>
        </div>

        {session.current.excludedFontIds.length > 0 ? (
          <details className="disclosure expert-restore"><summary>Restore a rejected family ({session.current.excludedFontIds.length})</summary><div className="disclosure-body restore-list">{session.current.excludedFontIds.map((fontId) => { const font = typeLibrary.fonts.find((item) => item.id === fontId); return <button type="button" className="btn" key={fontId} onClick={() => dispatch({ type: "typeStudioAction", action: { type: "restoreFont", fontId } })}>{font?.family ?? fontId}</button>; })}</div></details>
        ) : null}
      </div>
    </details>
  );
};

const ExactTypeResult = ({ session }: { session: TypeStudioSession }) => {
  const { dispatch } = useStudio();
  const direction = selectedTypeDirection(session);
  if (!direction) return null;

  return (
    <section className="exact-type-studio" aria-labelledby="type-result-title">
      <header className="exact-type-studio__head">
        <div>
          <p className="eyebrow">Your type direction · exact in this browser</p>
          <h3 id="type-result-title" style={{ fontFamily: stacksForDirection(direction).display }}>{direction.name}</h3>
          <p>{session.recommendation.headline}</p>
        </div>
        <div className="type-studio-actions" aria-label="Type direction history">
          <button type="button" className="btn btn--primary" onClick={() => dispatch({ type: "typeStudioAction", action: { type: "shuffle" } })}>Shuffle five directions</button>
          <button type="button" className="btn" disabled={session.past.length === 0} onClick={() => dispatch({ type: "typeStudioAction", action: { type: "undo" } })}>Undo</button>
          <button type="button" className="btn" disabled={session.future.length === 0} onClick={() => dispatch({ type: "typeStudioAction", action: { type: "redo" } })}>Redo</button>
        </div>
      </header>

      <div className="type-direction-rail" aria-label="Five type directions">
        {session.recommendation.directions.map((candidate, index) => {
          const active = candidate.id === direction.id;
          return (
            <button
              type="button"
              key={candidate.id}
              className="type-direction-ticket"
              data-active={active}
              aria-pressed={active}
              onClick={() => dispatch({ type: "typeStudioAction", action: { type: "selectDirection", directionId: candidate.id } })}
              style={{ "--ticket-font": stacksForDirection(candidate).display } as CSSProperties}
            >
              <span>{String(index + 1).padStart(2, "0")} · {candidate.laneLabel}</span>
              <strong>Aa</strong>
              <i>{candidate.name}</i>
            </button>
          );
        })}
      </div>

      <div className="type-role-workbench">
        {(["display", "body", "utility"] as const).map((role) => {
          const font = direction.roles[role];
          const locked = session.current.locked[role] === font.id;
          const starred = session.current.starredFontIds.includes(font.id);
          return (
            <article className="type-role-card" key={role} style={{ fontFamily: stacksForDirection(direction)[role] }}>
              <span className="type-role-card__label">{roleLabel(role)}</span>
              <strong>{role === "display" ? "Make the point." : role === "body" ? "The reading work stays calm enough to trust." : "Q3 · 48% · 07"}</strong>
              <div className="type-role-card__meta">
                <span><b>{font.family}</b> · {font.designer}</span>
                <span>{font.license.spdx} · self-hosted</span>
              </div>
              <div className="type-role-card__actions">
                <button type="button" aria-pressed={locked} onClick={() => dispatch({ type: "typeStudioAction", action: { type: "toggleLock", role, fontId: font.id } })}>{locked ? "Locked" : "Lock"}</button>
                <button type="button" aria-pressed={starred} onClick={() => dispatch({ type: "typeStudioAction", action: { type: "toggleStarFont", fontId: font.id } })}>{starred ? "★ Saved" : "☆ Save"}</button>
                <button type="button" onClick={() => dispatch({ type: "typeStudioAction", action: { type: "excludeFont", fontId: font.id } })}>Not this one</button>
              </div>
            </article>
          );
        })}
      </div>

      <div className="type-direction-why">
        <div><p className="eyebrow">Why this survives</p><ul>{direction.why.slice(0, 3).map((line) => <li key={line}>{line}</li>)}</ul></div>
        <div><p className="eyebrow">Do not do this</p><ul>{direction.cautions.map((line) => <li key={line}>{line}</li>)}</ul></div>
      </div>

      <ExpertTypeLab session={session} />

      <p className="truth-note">The letterforms above are exact local font files. That proves this browser preview—not installation, editable handoff, or export behaviour in your deck application.</p>
    </section>
  );
};

export const DirectionCardView = ({ direction, onOpenLab, onOpenBench: _onOpenBench, onOpenExport }: {
  direction: DirectionCard;
  onOpenLab: () => void;
  onOpenBench: () => void;
  onOpenExport: () => void;
}) => {
  const { state, dispatch } = useStudio();
  const type = direction.type;
  const exactDirection = selectedTypeDirection(state.typeStudio);
  const selectedType = type.recommendations.find((item) => item.systemId === direction.selected.typeSystemId) ?? type.recommendations[0] ?? null;
  const { copy: words } = previewCopyFor(state.brief);

  if (type.outcome === "boundary" || (type.outcome === "unsupported" && state.typeResult)) {
    return (
      <div className="result-boundary">
        <p className="eyebrow">We stopped for a reason</p>
        <h2 className="step-title">A real constraint beat a prettier lie.</h2>
        <p className="step-intro">{type.headline}</p>
        <div className="notice"><strong>The useful next move</strong><ul>{type.nextActions.map((action) => <li key={action}>{action}</li>)}</ul></div>
        <details className="disclosure"><summary>See what blocked the recommendation</summary><div className="disclosure-body"><ul>{type.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}</ul></div></details>
        <div className="flow-actions"><button type="button" className="btn btn--primary" onClick={() => dispatch({ type: "setStep", step: "brief" })}>Change my answers</button></div>
      </div>
    );
  }

  const legacySources = !exactDirection && selectedType ? fontSourcesForSystem(selectedType.systemId) : [];
  const savedExactFonts = exactFontsForRecommendation(selectedType);
  const displayFamily = exactDirection?.roles.display.family ?? savedExactFonts.display?.family ?? (selectedType ? legacySources.find((font) => font.id === selectedType.roles.display.fontId)?.family ?? selectedType.roles.display.fontId : null);
  const bodyFamily = exactDirection?.roles.body.family ?? savedExactFonts.body?.family ?? (selectedType ? legacySources.find((font) => font.id === selectedType.roles.body.fontId)?.family ?? selectedType.roles.body.fontId : null);
  const previewFonts = exactDirection ? stacksForDirection(exactDirection) : stacksForRecommendation(selectedType);
  const typePair = displayFamily && bodyFamily ? (displayFamily === bodyFamily ? displayFamily : `${displayFamily} + ${bodyFamily}`) : null;
  const headline = selectedType ? `${typePair ?? selectedType.name}.` : "Your type direction is ready.";
  const introduction = selectedType
    ? `${displayFamily} leads. ${bodyFamily} does the reading work. Every letterform below is the actual font—and the fitting room lets you challenge every choice.`
    : "A useful starting point you can inspect, change and take with you.";
  const slideTruthLabel = exactDirection
    ? "A real 16:9 slide · exact browser fonts"
    : selectedType
      ? "A real 16:9 slide · saved type direction"
      : "A real 16:9 slide · your saved type direction";

  return (
    <div className="direction-result">
      <header className="result-head">
        <p className="eyebrow">Made for this deck</p>
        <h2 className="step-title">{headline}</h2>
        <p className="step-intro">{introduction}</p>
      </header>

      <div className="result-grid">
        {state.typeStudio ? <ExactTypeResult session={state.typeStudio} /> : selectedType ? <SavedTypeResult item={selectedType} /> : null}
      </div>

      <div className="result-slide-stage">
        <div className="result-slide-aura" aria-hidden="true"><span /><span /><span /></div>
        <SlidePreview
          title={words.slideTitle ?? words.deckTitle ?? "Make the point visible."}
          body={words.body ?? "The slide carries one thought. The detail earns its place by helping that thought land."}
          kicker={words.deckTitle && words.deckTitle !== words.slideTitle ? words.deckTitle : "YOUR DECK"}
          fonts={previewFonts}
          typography={state.typeTuning}
          variant="titleBody"
          label={slideTruthLabel}
        />
      </div>

      <div className="result-actions">
        <button type="button" className="btn btn--primary" onClick={onOpenLab}>Try five real slides</button>
        <button type="button" className="btn" onClick={onOpenExport}>Take the direction away</button>
      </div>

      <details className="disclosure technical-details">
        <summary>Why this answer—and what could break it</summary>
        <div className="disclosure-body technical-stack">
          {exactDirection ? <section><h3>Type caution</h3><ul>{exactDirection.cautions.map((line) => <li key={line}>{line}</li>)}</ul></section> : selectedType ? <section><h3>Type caution</h3><p>{selectedType.oneThingNotToDo}</p></section> : null}
          <EvidenceDrawer direction={direction} />
        </div>
      </details>
    </div>
  );
};
